import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import MoreContentRequester from 'components/MoreContentRequester';
import CircularProgress from 'components/shared/CircularProgress';
import { useMenuContext } from 'components/shared/menu';
import ThinSearch from 'components/shared/ThinSearch';
import Tabs, { TabType } from 'components/Tabs';
import { useDebounce } from 'hooks/useDebounce';
import useNow from 'hooks/useNow';
import Notification from 'models/Notification';
import {
  useInfiniteNotifications,
  useMarkAllNotificationsRead,
  useNotificationUnreadCount,
  useUpdateNotification,
} from 'services/api/notification';
import { colors } from 'styles/theme/colors';

import NotificationEmptyState from './NotificationEmptyState';
import NotificationRow from './NotificationRow';

type InboxTab = 'unread' | 'all';

const TABS: readonly TabType[] = [
  { label: 'Unread', value: 'unread', 'data-cy': 'notificationsTabUnread' },
  { label: 'All', value: 'all', 'data-cy': 'notificationsTabAll' },
];

type Props = {
  enabled: boolean;
  // Mutations are owned by the always-mounted NotificationBell, not here: opening a row closes
  // the panel (unmounting this component), and a TanStack observer's onSuccess (our invalidation)
  // would not fire if the owner unmounted mid-flight. The bell persists across in-app navigation.
  updateNotification: ReturnType<typeof useUpdateNotification>;
  markAllRead: ReturnType<typeof useMarkAllNotificationsRead>;
};

export default function NotificationInbox({ enabled, updateNotification, markAllRead }: Props) {
  const navigate = useNavigate();
  const { toggle } = useMenuContext();
  const now = useNow();

  const [activeTab, setActiveTab] = useState<InboxTab>('unread');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const { data: unreadCount = 0 } = useNotificationUnreadCount({ enabled });

  // Unread tab sends `unread=true`; All sends nothing. Search (server-side, combined
  // patient-name + message) applies to the active tab.
  const queryParams = useMemo(
    () => ({
      ...(activeTab === 'unread' ? { unread: true } : {}),
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    }),
    [activeTab, debouncedSearch]
  );

  const query = useInfiniteNotifications({ enabled }, queryParams);

  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);
  const isEmpty = !query.isLoading && rows.length === 0;

  // Open a row's target: mark read (if unread) + close the panel + SPA-navigate — one action
  // (§5 item 3). Cross-client targets switch the acting client via the destination route's
  // existing useActingClientAuthorizedCheck gate (§5 item 10); the row just navigates.
  const handleOpen = (notification: Notification) => {
    if (notification.isUnread) {
      updateNotification.mutate({ id: notification.id, read: true });
    }
    toggle(false);
    const { targetUrl } = notification.notificationEvent;
    if (targetUrl) navigate(targetUrl);
  };

  const handleToggleRead = (notification: Notification) => {
    updateNotification.mutate({ id: notification.id, read: notification.isUnread });
  };

  return (
    <Container data-testid='notificationInbox' onClick={(e) => e.stopPropagation()}>
      <Header>
        <Title data-testid='inboxTitle'>Inbox ({unreadCount})</Title>
        <HeaderActions>
          <MarkAllLink
            type='button'
            disabled={unreadCount === 0}
            onClick={() => markAllRead.mutate()}
            data-testid='markAllRead'>
            Mark all as read
          </MarkAllLink>
          <CloseButton type='button' aria-label='Close' onClick={() => toggle(false)} data-testid='closeInbox'>
            &times;
          </CloseButton>
        </HeaderActions>
      </Header>

      <SearchRow>
        <ThinSearch placeholder='Search notifications' value={search} onChange={setSearch} />
      </SearchRow>

      <TabsRow>
        <Tabs
          tabs={TABS}
          selectedTab={TABS.find((tab) => tab.value === activeTab)}
          onTabClick={(tab) => setActiveTab(tab.value as InboxTab)}
        />
      </TabsRow>

      <List data-testid='notificationsList'>
        {query.isLoading && (
          <Centered>
            <CircularProgress color={colors.blue100} />
          </Centered>
        )}

        {rows.map((notification) => (
          <NotificationRow
            key={notification.id}
            notification={notification}
            now={now}
            onOpen={handleOpen}
            onToggleRead={handleToggleRead}
          />
        ))}

        <MoreContentRequester resource={query} />

        {isEmpty && <NotificationEmptyState tab={activeTab} searching={!!debouncedSearch} />}
      </List>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-height: min(80vh, 620px);
`;

const Header = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 24px 24px 12px;
`;

const Title = styled.span`
  color: ${colors.black100};
  font-size: 16px;
  font-weight: 600;
`;

const HeaderActions = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
`;

const MarkAllLink = styled.button`
  background: none;
  border: none;
  color: ${colors.blue100};
  cursor: pointer;
  font-size: 12px;
  padding: 0;
  text-decoration: underline;

  &:disabled {
    color: ${colors.black25};
    cursor: default;
    text-decoration: none;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${colors.black50};
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  padding: 0;
`;

const SearchRow = styled.div`
  padding: 0 24px 12px;
`;

const TabsRow = styled.div`
  border-bottom: 1px solid ${colors.black10};
  padding: 0 24px;
`;

const List = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;
